import {useEquityIssuersQuery} from '@transactions/graphql';
import {fromSeconds, today} from 'common/date';
import * as React from 'react';
import {useBreadcrumb} from 'src/Breadcrumbs';
import {EquityChartPanel} from 'src/EquityView/EquityChartPanel';
import {
  buildSaleTotals,
  buildTotals,
  formatCents,
  issuerCurrency,
  latestMark,
} from 'src/EquityView/equitySeries';
import {EventsList} from 'src/EquityView/EventsList';
import {LotsTable} from 'src/EquityView/LotsTable';
import {cn} from 'src/lib/utils';

const StatTile = ({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail?: string;
}) => (
  <div className="border-border rounded-md border p-3">
    <div className="text-muted-foreground text-xs">{label}</div>
    <div className="text-foreground text-lg font-semibold tabular-nums">
      {value}
    </div>
    {detail && <div className="text-muted-foreground text-xs">{detail}</div>}
  </div>
);

/**
 * Private-company equity dashboard: per-issuer value history (with the
 * never-sold counterfactual), the lot ledger, and every liquidity event's
 * money waterfall. Read-only v1 — data arrives via `import:equity` and the
 * equity mutations; no editing UI yet.
 */
export const EquityView = () => {
  useBreadcrumb({label: 'Equity', reset: true});
  const {data, loading, error} = useEquityIssuersQuery({
    fetchPolicy: 'cache-and-network',
  });
  const issuers = React.useMemo(() => data?.equityIssuers ?? [], [data]);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const issuer =
    issuers.find((candidate) => candidate._id === selectedId) ??
    issuers[0] ??
    null;
  const todaySeconds = React.useMemo(() => today().toSeconds(), []);

  if (error) {
    return (
      <div className="text-destructive p-8 text-center text-sm">
        Couldn't load equity: {error.message}
      </div>
    );
  }
  if (loading && data == null) {
    return (
      <div className="text-muted-foreground p-8 text-center text-sm">
        Loading…
      </div>
    );
  }

  if (issuer == null) {
    return (
      <div className="text-muted-foreground p-12 text-center text-sm">
        No equity recorded yet. Run{' '}
        <code className="bg-muted rounded px-1 py-0.5">
          yarn workspace server import:equity &lt;manifest.json&gt; --user &lt;id&gt;
        </code>{' '}
        to load an issuer's marks, lots, and events.
      </div>
    );
  }

  const totals = buildTotals(issuer, todaySeconds);
  const sales = buildSaleTotals(issuer, todaySeconds);
  const latest = latestMark(issuer, todaySeconds);
  // A fully sold issuer's held value is a known zero even with no marks;
  // and the server's valueCents follows the LATEST mark's currency, so a
  // sum with the sale cents is only meaningful when the currencies agree.
  const heldCents =
    totals.valueCents ?? (totals.sharesHeld === 0 ? 0 : null);
  const heldCurrencyOk =
    totals.sharesHeld === 0 ||
    latest == null ||
    latest.price.currency === issuerCurrency(issuer);
  const cents = (value: number): string =>
    formatCents(value, issuerCurrency(issuer));
  // Recorded per-share endpoints keep SharePrice's six decimals; the
  // blended average is a derived figure and rounds to cents.
  const perShare = (value: number, maxDigits = 2): string =>
    value.toLocaleString('en-US', {
      style: 'currency',
      currency: issuerCurrency(issuer),
      maximumFractionDigits: maxDigits,
    });

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="mx-auto flex max-w-5xl flex-col gap-4 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className="text-foreground text-xl font-semibold">
              {issuer.name} equity
            </h1>
            <p className="text-muted-foreground text-sm">
              Share history, price marks, and liquidity events.
            </p>
          </div>
          {issuers.length > 1 && (
            <div className="flex flex-wrap gap-1.5">
              {issuers.map((candidate) => (
                <button
                  key={candidate._id}
                  type="button"
                  onClick={() => setSelectedId(candidate._id)}
                  className={cn(
                    'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                    candidate._id === issuer._id
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border text-muted-foreground hover:text-foreground',
                  )}
                >
                  {candidate.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Three pre-tax anchors: cash taken out, value still in, their sum.
            Kin to the chart's realized + held line but stricter: the line
            also counts tax-withheld share value and estimates undocumented
            proceeds, while these tiles only assert documented figures.
            Basis/unrealized detail lives in the lots footer; per-sale money
            lives on the event cards. */}
        <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
          <StatTile
            label="Cashed out (gross)"
            value={
              sales.eventCount === 0
                ? cents(0)
                : sales.grossCents != null
                  ? cents(sales.grossCents)
                  : '—'
            }
            detail={`pre-tax · ${sales.sharesSold.toLocaleString('en-US')} sh sold${
              sales.avgPriceDollars != null
                ? ` · avg ${perShare(sales.avgPriceDollars)}/sh${
                    sales.documentedSharesSold !== sales.sharesSold
                      ? ` (over ${sales.documentedSharesSold.toLocaleString('en-US')} documented sh)`
                      : ''
                  }`
                : ''
            }${
              sales.undocumentedCount > 0
                ? ` · excl. ${sales.undocumentedCount} undocumented`
                : ''
            }${
              sales.excludedCurrencyCount > 0
                ? ` · excl. ${sales.excludedCurrencyCount} other-currency`
                : ''
            }${
              sales.estimatedCount > 0
                ? ` · ${sales.estimatedCount} estimated`
                : ''
            }`}
          />
          <StatTile
            label="Still held"
            value={heldCents != null && heldCurrencyOk ? cents(heldCents) : '—'}
            detail={`${totals.sharesHeld.toLocaleString('en-US')} sh${
              latest
                ? ` · at the ${latest.price.format()} mark · ${fromSeconds(
                    latest.date,
                  ).toLocaleString({month: 'short', year: 'numeric'})}`
                : ''
            }`}
          />
          <StatTile
            label="Total value"
            value={
              heldCents != null && heldCurrencyOk
                ? sales.grossCents != null
                  ? cents(sales.grossCents + heldCents)
                  : sales.eventCount === 0
                    ? cents(heldCents)
                    : '—'
                : '—'
            }
            detail="cashed out + still held · pre-tax"
          />
        </div>

        <EquityChartPanel issuer={issuer} />
        <LotsTable issuer={issuer} todaySeconds={todaySeconds} />
        <EventsList issuer={issuer} todaySeconds={todaySeconds} />
      </div>
    </div>
  );
};
